import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, RefreshCw, Layers } from "lucide-react";
import useAuthStore from "@/store/authStore";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Job Work Lifecycle Phase 1 (Jul 2026) — read-only "Stock at Job Worker"
// visibility page. Consumes GET /api/jobwork/balances (backend hydrates
// each row with vendor_name + rm_name + rm_category, plus dispatched /
// consumed / closing per AC #4).
export default function JobWorkBalances() {
  const { token } = useAuthStore();
  const getHeaders = useCallback(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorFilter, setVendorFilter] = useState("ALL");
  const [rmFilter, setRmFilter] = useState("");
  const [showZero, setShowZero] = useState(false);

  const fetchVendors = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vendors?is_job_worker=true`, { headers: getHeaders() });
      setVendors(Array.isArray(res.data) ? res.data : []);
    } catch (_e) {
      // Non-fatal: filter dropdown just stays empty.
    }
  }, [getHeaders]);

  const fetchBalances = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (vendorFilter !== "ALL") params.set("vendor_id", vendorFilter);
      if (showZero) params.set("include_zero", "true");
      const res = await axios.get(
        `${API}/jobwork/balances?${params.toString()}`,
        { headers: getHeaders() },
      );
      setBalances(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load balances");
    } finally {
      setLoading(false);
    }
  }, [vendorFilter, showZero, getHeaders]);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);
  useEffect(() => { fetchBalances(); }, [fetchBalances]);

  const rows = useMemo(() => {
    if (!rmFilter) return balances;
    const q = rmFilter.toLowerCase();
    return balances.filter((r) =>
      (r.rm_id || "").toLowerCase().includes(q) ||
      (r.rm_name || "").toLowerCase().includes(q) ||
      (r.vendor_name || "").toLowerCase().includes(q),
    );
  }, [balances, rmFilter]);

  const totalClosing = useMemo(
    () => rows.reduce((sum, r) => sum + Number(r.closing || 0), 0),
    [rows],
  );

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="job-work-balances-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Layers className="w-7 h-7 text-orange-500" />
            Job Work Balances
          </h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Live snapshot of Bidso-supplied stock currently held at job-worker
            vendors. Balances credit on job-work Delivery Challan dispatch and
            (from Phase 2) debit on receipt.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchBalances}
          disabled={loading}
          data-testid="jw-refresh-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="text-xs font-semibold uppercase text-zinc-500">Vendor</Label>
              <Select value={vendorFilter} onValueChange={setVendorFilter}>
                <SelectTrigger data-testid="jw-vendor-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All job-worker vendors</SelectItem>
                  {vendors.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-zinc-500">Search RM / Vendor</Label>
              <Input
                placeholder="rm_id, name or vendor…"
                value={rmFilter}
                onChange={(e) => setRmFilter(e.target.value)}
                data-testid="jw-search-input"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-zinc-500">Include Zero</Label>
              <label className="flex items-center gap-2 h-10">
                <input
                  type="checkbox"
                  checked={showZero}
                  onChange={(e) => setShowZero(e.target.checked)}
                  className="w-4 h-4 accent-orange-500"
                  data-testid="jw-include-zero"
                />
                <span className="text-sm">Show closed rows (balance = 0)</span>
              </label>
            </div>
            <div className="text-right text-xs text-zinc-500">
              <div>
                Rows: <strong data-testid="jw-row-count">{rows.length}</strong>
              </div>
              <div>
                Total Closing: <strong data-testid="jw-total-closing">{Number(totalClosing).toLocaleString("en-IN", { maximumFractionDigits: 3 })}</strong>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Open Balances ({rows.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-zinc-500">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-zinc-500" data-testid="jw-empty">
              No open balances. Once a job-work Delivery Challan ships, its
              Bidso-supplied lines will appear here.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-xs uppercase text-zinc-500 tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Vendor</th>
                  <th className="px-4 py-3 text-left">RM</th>
                  <th className="px-4 py-3 text-left">Category</th>
                  <th className="px-4 py-3 text-left">Origin Branch</th>
                  <th className="px-4 py-3 text-right">Dispatched</th>
                  <th className="px-4 py-3 text-right">Consumed</th>
                  <th className="px-4 py-3 text-right">Scrapped</th>
                  <th className="px-4 py-3 text-right">Returned</th>
                  <th className="px-4 py-3 text-right">Closing</th>
                  <th className="px-4 py-3 text-left">Updated</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id || `${r.vendor_id}-${r.rm_id}-${r.origin_branch}`}
                    className="border-t border-zinc-100 hover:bg-zinc-50/60"
                    data-testid={`jw-row-${r.vendor_id}-${r.rm_id}`}
                  >
                    <td className="px-4 py-3">{r.vendor_name || r.vendor_id}</td>
                    <td className="px-4 py-3 font-mono text-xs">
                      <div className="font-bold text-zinc-800">{r.rm_id}</div>
                      <div className="text-[11px] text-zinc-500 font-sans">{r.rm_name || "—"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono px-2 py-0.5 bg-zinc-100 rounded">
                        {r.rm_category || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs">{r.origin_branch || "—"}</td>
                    <td className="px-4 py-3 font-mono text-right">
                      {Number(r.dispatched || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 })}
                    </td>
                    <td className="px-4 py-3 font-mono text-right text-zinc-400">
                      {Number(r.consumed || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 })}
                    </td>
                    <td className="px-4 py-3 font-mono text-right text-amber-700">
                      {Number(r.scrapped || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 })}
                    </td>
                    <td className="px-4 py-3 font-mono text-right text-blue-700">
                      {Number(r.returned || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 })}
                    </td>
                    <td className="px-4 py-3 font-mono text-right font-bold text-emerald-700">
                      {Number(r.closing || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 })}
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-500">
                      {r.updated_at ? new Date(r.updated_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <p className="text-[11px] text-zinc-500">
        Register view. Closing = Dispatched − Consumed − Scrapped − Returned.
        Detail per receipt/scrap/return event ships in the Job Work Receipts page.
      </p>
    </div>
  );
}
